import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Lock, User, Eye, EyeOff, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/orgApi";
import { USR } from "@/constants/testIds";

const EMR_LOGO = `${process.env.PUBLIC_URL}/emrbymd-logo.png`;
const SHOW_DEMO_SHORTCUTS = process.env.NODE_ENV !== "production";

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [demoAccounts, setDemoAccounts] = useState([]);

  useEffect(() => {
    if (!SHOW_DEMO_SHORTCUTS) return;
    let cancelled = false;
    api.get("/auth/demo-accounts")
      .then((r) => {
        if (cancelled) return;
        const items = r.data?.enabled ? (r.data.items || []) : [];
        setDemoAccounts(items);
      })
      .catch(() => {
        if (!cancelled) setDemoAccounts([]);
      });
    return () => { cancelled = true; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    const liveEmail = (form.querySelector("#email")?.value || email || "").trim();
    const livePassword = (form.querySelector("#password")?.value || password || "").replace(/\r/g, "").trim();
    return signIn(liveEmail, livePassword);
  };

  const signIn = async (userEmail, userPassword) => {
    setError(""); setBusy(true);
    try {
      await login(userEmail, userPassword);
      nav("/select-context");
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed");
    } finally { setBusy(false); }
  };

  const applyDemoAccount = async (account) => {
    setEmail(account.email);
    setPassword(account.password);
    await signIn(account.email, account.password);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-100 px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-6 flex flex-col items-center">
          <span className="mb-6 rounded-full bg-[#1e3a8a] px-4 py-1 text-xs font-semibold tracking-widest text-white">STEP 1 · EMR LOGIN</span>
          <img src={EMR_LOGO} alt="EMRbyMD" className="h-14 w-auto" />
        </div>
        <form onSubmit={submit} data-testid={USR.loginForm} autoComplete="off"
          className="rounded-2xl border border-slate-200 bg-white p-8 shadow-xl">
          <div className="mb-6 flex flex-col items-center text-center">
            <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-blue-50 text-[#1e3a8a]">
              <Lock className="h-6 w-6" />
            </div>
            <h1 className="text-xl font-bold text-slate-900">Welcome Back</h1>
            <p className="text-sm text-slate-500">Sign in to access your EMR</p>
          </div>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-sm font-semibold text-slate-700">Email</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input id="email" name="email" data-testid={USR.loginEmail} value={email} autoComplete="username"
                  onChange={(e) => setEmail(e.target.value)} onInput={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email" className="pl-9" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-sm font-semibold text-slate-700">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input id="password" name="password" type={show ? "text" : "password"} data-testid={USR.loginPassword}
                  value={password} autoComplete="new-password"
                  onChange={(e) => setPassword(e.target.value)} onInput={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password" className="pl-9 pr-10" />
                <button type="button" data-testid={USR.loginPasswordToggle} onClick={() => setShow((s) => !s)}
                  aria-label={show ? "Hide password" : "Show password"} aria-pressed={show}
                  className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-slate-400 hover:text-[#1e3a8a] focus:outline-none focus:ring-2 focus:ring-blue-500/40">
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            {error && <p data-testid={USR.loginError} className="text-sm text-rose-600">{error}</p>}
            <Button type="submit" disabled={busy} data-testid={USR.loginSubmit}
              className="w-full bg-[#1d4ed8] py-6 text-base hover:bg-[#1e40af]">
              <Lock className="mr-2 h-4 w-4" /> {busy ? "Signing in…" : "Sign In"}
            </Button>
            <p className="text-center text-sm text-slate-500">Need help? Contact your <span className="font-semibold text-[#1d4ed8]">system administrator</span>.</p>
          </div>

          {SHOW_DEMO_SHORTCUTS && demoAccounts.length > 0 && (
            <div data-testid={USR.loginDemoPanel} className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4">
              <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-slate-500">
                Demo / Development Accounts
              </p>
              <div className="space-y-2">
                {demoAccounts.map((account) => (
                  <div key={account.id} className="flex items-center justify-between gap-3 rounded-lg bg-white px-3 py-2 ring-1 ring-slate-100">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-slate-800">{account.label}</p>
                      <p className="truncate font-mono text-xs text-slate-500">{account.email}</p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={busy}
                      data-testid={`${USR.loginDemoBtn}-${account.id}`}
                      onClick={() => applyDemoAccount(account)}
                      className="shrink-0"
                    >
                      Use Demo Account
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </form>
        <div className="mt-4 flex items-start gap-3 rounded-xl bg-blue-50/70 p-4 text-sm text-slate-600">
          <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-[#1d4ed8]" />
          <p><span className="font-semibold text-slate-800">Why two steps?</span> For security and clarity, we first verify your identity. Next, you'll choose where you want to work.</p>
        </div>
      </div>
    </div>
  );
}
